import { useMutation, } from '@tanstack/react-query';
import { FetchApiError, RecoverableApiError, } from '@zurich-framework/core/errors';
import { useCallback } from 'react';
import { useSnackbar } from '../useSnackbar';
import { useFetchApi, } from './useFetchApi';
const TIMEOUT_WARNING_MS = 10000; // 10 seconds
function getDataState(status) {
    switch (status) {
        case 'idle':
            return 'unset';
        default:
            return status;
    }
}
/**
 * React Query mutation hook for sending data to the API.
 *
 * Use this hook if:
 * - you want to send data to the API (create, update, delete)
 * - you need to fetch data inside an event callback like onClick and you need success or error handling inside the same closure
 *
 * Prefer `useApiQuery` if you want to query data from the API to display it in the UI.
 */
export function useApi({ request, options: { timeoutWarningMs = TIMEOUT_WARNING_MS, handleError, errorText, successText, ...otherOptions }, mutationOptions = {}, }) {
    const { fetchApi, showApiError } = useFetchApi();
    const { flashSuccess } = useSnackbar();
    const { data, status, mutateAsync } = useMutation(
    // mutationFn
    async (params) => {
        const res = await fetchApi(request, params, {
            ...otherOptions,
            throwOnError: true,
            timeoutWarningMs,
            errorText,
        });
        // this should never happen, because we always throw on errors
        // if a code change results in an errornous response here, we throw again with an unhandled error
        if (res === null) {
            throw new Error('Unhandled fetchApi error in mutation hook');
        }
        return res;
    }, 
    // options
    mutationOptions);
    const fetch = useCallback(async ({ params, onSuccess, onRecoverableError, onError }) => mutateAsync(params, {
        onSuccess: (data, variables) => {
            // default success handling is showing a snackbar success message, if successText is provided
            if (successText) {
                flashSuccess({ message: successText });
            }
            onSuccess?.(data, variables);
        },
        onError: (error, variables) => {
            // if the error is thrown as recoverable, we call the onRecoverableError callback instead
            if (error instanceof RecoverableApiError) {
                onRecoverableError?.(error, variables);
                return;
            }
            // default error handling is showing a snackbar error
            if (error instanceof FetchApiError) {
                if (handleError) {
                    handleError(error, (props) => {
                        showApiError({
                            text: errorText,
                            retryCallback: async () => {
                                await mutateAsync(variables);
                            },
                            ...props,
                        });
                    });
                }
                else {
                    showApiError({
                        text: errorText,
                        retryCallback: async () => {
                            await mutateAsync(variables);
                        },
                        error,
                    });
                }
            }
            onError?.(error, variables);
        },
    }), [
        errorText,
        flashSuccess,
        handleError,
        mutateAsync,
        showApiError,
        successText,
    ]);
    return { data, dataState: getDataState(status), fetch };
}
